// Brochure assets per resort.
// Lombok uses /brochure/lombok/ (Indonesia PDF, Feb 2026) for apartment interior renders.
// Spille reuses Lombok interior renders 1:1 (identical architectural product).
//   Spille-exclusive common areas (Lobby, Fine Dining, Banchetti, Rooftop) are served
//   from Emergent customer-assets via `gallery_groups` in the property model.
// Chumphon reuses Lombok suite imagery (identical architectural product) +
// 2 exclusive exterior renderings provided by the team.
// All apartment assets served statically from /public.

const spille    = '/brochure/spille/';
const lombok    = '/brochure/lombok/';
const chumphon  = '/brochure/chumphon/';

// Common pricing & metrature (same architectural grid for all resorts)
const PRICE_GRID = {
  standard:    { sqm: 82.5,  pricePerSqm: 2500, price: 152500 },
  king:        { sqm: 102.3, pricePerSqm: 2500, price: 222500 },
  'king-pool': { sqm: 102.3, pricePerSqm: 2700, price: 283500 },
  rooftop:     { sqm: 238.5, pricePerSqm: 3000, price: 615000 },
};

const APT_DESCRIPTIONS = {
  standard: {
    tagline: 'Lusso compatto · ingresso strategico',
    description: 'Ingombro compatto di lusso, ideale per soggiorni vacanzieri. Posizionamento strategico dell\u2019ingresso. Accessibile, senza compromessi.',
    highlights: ['Layout compatto ottimizzato', 'Ingresso indipendente strategico', 'Ideale vacanze / short-let'],
  },
  king: {
    tagline: 'Raffinatezza versatile · 2 camere open-space',
    description: 'Layout a due camere con soggiorno open-space. Efficienza spaziale ottimizzata. Un layout raffinato e versatile, progettato sia per lo stile di vita personale sia per la locazione.',
    highlights: ['2 camere da letto', 'Soggiorno open-space', 'Stile di vita o locazione'],
  },
  'king-pool': {
    tagline: 'Piscina privata · indoor/outdoor armonico',
    description: 'Layout con due camere da letto, flusso equilibrato tra interno ed esterno. Progettato per uno stile di vita versatile o per la locazione premium.',
    highlights: ['2 camere da letto', 'Piscina privata', 'Flusso indoor-outdoor'],
  },
  rooftop: {
    tagline: 'Privacy elevata · 4 camere · vista 360\u00b0',
    description: 'Quattro camere da letto principali, soggiorno open-space, cucina a isola, doppi bagni. Massima privacy con spazi abitativi ampi e generosi.',
    highlights: ['4 camere master', 'Cucina a isola', 'Doppi bagni · Theatre Room'],
  },
};

const buildType = (key, name, rooms) => ({
  key,
  name,
  ...PRICE_GRID[key],
  ...APT_DESCRIPTIONS[key],
  rooms,
});

// ===================== SPILLE / ALBANIA =====================
// NOTE: Spille apartment interiors are *identical* to Lombok (same architectural product).
// Per cliente, i blocchi Spille non hanno la cantina vini che troviamo a Lombok,
// ma le 4 tipologie suite sono visualmente identiche \u2014 quindi riutilizziamo LOMBOK_TYPES.
// Le aree comuni esclusive (Lobby, Fine Dining, Banchetti, Rooftop) sono rese tramite
// `gallery_groups` del property model (vedi /app/backend/seed.py) con render dedicati.
const SPILLE_TYPES = []; // populated below after LOMBOK_TYPES is declared

const SPILLE_SHOWCASE = [
  // Spille showcase aree (masterplan, filosofia, architettura, ristorante, spa-gym)
  // intentionally emptied: queste sezioni sono ora coperte con render reali nuovi
  // tramite il campo `gallery_groups` del property model (vedi seed.py).
  // Le 4 tipologie appartamenti riutilizzano LOMBOK_TYPES (camere identiche all'Indonesia).
];

// ===================== LOMBOK / INDONESIA =====================
const LOMBOK_TYPES = [
  buildType('standard', 'Standard Suite', [
    { src: lombok + 'p28_01.jpg', caption: 'Standard Suite \u00b7 Overview' },
    { src: lombok + 'p29_01.jpg', caption: 'Standard Suite \u00b7 Camera' },
    { src: lombok + 'p29_03.jpg', caption: 'Standard Suite \u00b7 Bagno' },
  ]),
  buildType('king', 'King Suite', [
    { src: lombok + 'p25_01.jpg', caption: 'King Suite \u00b7 Overview' },
    { src: lombok + 'p26_01.jpg', caption: 'King Suite \u00b7 Soggiorno' },
    { src: lombok + 'p26_03.jpg', caption: 'King Suite \u00b7 Camera padronale' },
    { src: lombok + 'p27_01.jpg', caption: 'King Suite \u00b7 Camera 2' },
    { src: lombok + 'p27_03.jpg', caption: 'King Suite \u00b7 Camera 2 (vista alternativa)' },
  ]),
  buildType('king-pool', 'King Pool Suite', [
    { src: lombok + 'p22_01.jpg', caption: 'King Pool \u00b7 Overview' },
    { src: lombok + 'p23_01.jpg', caption: 'King Pool \u00b7 Camera padronale' },
    { src: lombok + 'p23_03.jpg', caption: 'King Pool \u00b7 Master suite (vista 2)' },
    { src: lombok + 'p24_01.jpg', caption: 'King Pool \u00b7 Camera 2' },
    { src: lombok + 'p24_03.jpg', caption: 'King Pool \u00b7 Camera 2 (vista alternativa)' },
  ]),
  buildType('rooftop', 'Rooftop Suite', [
    { src: lombok + 'p16_01.jpg', caption: 'Rooftop Suite \u00b7 Overview' },
    { src: lombok + 'p17_01.jpg', caption: 'Rooftop Suite \u00b7 Soggiorno' },
    { src: lombok + 'p17_03.jpg', caption: 'Rooftop Suite \u00b7 Soggiorno (vista 2)' },
    { src: lombok + 'p18_01.jpg', caption: 'Rooftop Suite \u00b7 Cucina e sala da pranzo' },
    { src: lombok + 'p18_03.jpg', caption: 'Rooftop Suite \u00b7 Cucina (vista 2)' },
    { src: lombok + 'p19_01.jpg', caption: 'Rooftop Suite \u00b7 Bagno' },
    { src: lombok + 'p19_03.jpg', caption: 'Rooftop Suite \u00b7 Sala cinema' },
    { src: lombok + 'p20_01.jpg', caption: 'Rooftop Suite \u00b7 Camera padronale' },
    { src: lombok + 'p21_01.jpg', caption: 'Rooftop Suite \u00b7 Camera 2' },
    { src: lombok + 'p21_03.jpg', caption: 'Rooftop Suite \u00b7 Camera 3' },
  ]),
];

const LOMBOK_SHOWCASE = [
  // Lombok showcase aree (masterplan, architettura, ristorante, spa-gym, development)
  // intentionally emptied: queste sezioni sono ora coperte con render reali nuovi
  // tramite il campo `gallery_groups` del property model (vedi seed.py).
  // Le 4 tipologie appartamenti restano in LOMBOK_TYPES sopra.
];

// Spille apartment types reuse Lombok 1:1 (identical architectural product).
// Must come AFTER LOMBOK_TYPES is declared to avoid TDZ errors.
SPILLE_TYPES.push(...LOMBOK_TYPES);

// ===================== CHUMPHON / THAILANDIA =====================
// Solo 2 tipologie: King Suite (piano terra) + Standard Suite (primo piano, 3 unità per blocco).
// Stesse foto Lombok perché architettonicamente sono identiche, ma niente King Pool né Rooftop.
const CHUMPHON_TYPES = LOMBOK_TYPES.filter((t) => ['standard', 'king'].includes(t.key));

const CHUMPHON_SHOWCASE = [
  // Chumphon showcase aree (esterni) intentionally emptied: la sezione esterni
  // \u00e8 ora servita tramite il campo `gallery_groups` della property model in seed.py
  // (con 3 render: il nuovo signature shot + 2 dalla brochure ufficiale).
  // Le 2 tipologie appartamenti (Suite + King Suite) usano LOMBOK_TYPES filtered.
];

// =================== EXPORT ===================
export const BROCHURE_DATA = {
  spille:   { apartmentTypes: SPILLE_TYPES,   showcase: SPILLE_SHOWCASE,   hero: spille + 'p04_01.jpg' },
  lombok:   { apartmentTypes: LOMBOK_TYPES,   showcase: LOMBOK_SHOWCASE,   hero: lombok + 'p02_01.jpg' },
  chumphon: { apartmentTypes: CHUMPHON_TYPES, showcase: CHUMPHON_SHOWCASE, hero: chumphon + 'exterior_01.jpg' },
};

// Backwards compatibility
export const APARTMENT_TYPES_V2 = SPILLE_TYPES;
export const RESORT_SHOWCASE = SPILLE_SHOWCASE;
export const BROCHURE_HERO = spille + 'p04_01.jpg';
